import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { apiClient, User, AuthResponse } from '../../services/api';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  profileLoading: boolean;
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  token: localStorage.getItem('auth_token'),
  isAuthenticated: !!localStorage.getItem('auth_token'),
  loading: false,
  profileLoading: false,
  error: null,
};

// 异步actions
export const register = createAsyncThunk(
  'auth/register',
  async ({
    email,
    password,
    username,
  }: {
    email: string;
    password: string;
    username?: string;
  }) => {
    const response = await apiClient.register(email, password, username);
    return response.data;
  }
);

export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password }: { email: string; password: string }) => {
    const response = await apiClient.login(email, password);
    return response.data;
  }
);

export const getProfile = createAsyncThunk('auth/getProfile', async () => {
  const response = await apiClient.getProfile();
  return response.data;
});

export const updatePreferences = createAsyncThunk(
  'auth/updatePreferences',
  async (preferences: Record<string, unknown>) => {
    const response = await apiClient.updatePreferences(preferences);
    return response.data;
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: state => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      apiClient.clearToken();
    },
    clearError: state => {
      state.error = null;
    },
  },
  extraReducers: builder => {
    builder
      // register
      .addCase(register.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        register.fulfilled,
        (state, action: PayloadAction<AuthResponse>) => {
          state.loading = false;
          state.user = action.payload.user;
          state.token = action.payload.token;
          state.isAuthenticated = true;
          apiClient.setToken(action.payload.token);
        }
      )
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Registration failed';
      })
      // login
      .addCase(login.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        login.fulfilled,
        (state, action: PayloadAction<AuthResponse>) => {
          state.loading = false;
          state.user = action.payload.user;
          state.token = action.payload.token;
          state.isAuthenticated = true;
          apiClient.setToken(action.payload.token);
        }
      )
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Login failed';
      })
      // getProfile
      .addCase(getProfile.pending, state => {
        state.profileLoading = true;
      })
      .addCase(getProfile.fulfilled, (state, action: PayloadAction<User>) => {
        state.user = action.payload;
        state.profileLoading = false;
      })
      .addCase(getProfile.rejected, state => {
        // Token可能已过期，清除认证状态
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        apiClient.clearToken();
        state.profileLoading = false;
      })
      // updatePreferences
      .addCase(
        updatePreferences.fulfilled,
        (state, action: PayloadAction<User>) => {
          state.user = action.payload;
        }
      );
  },
});

export const { logout, clearError } = authSlice.actions;
export default authSlice.reducer;
